import AwesomeDebouncePromise from "awesome-debounce-promise";
import { AxiosResponse } from "axios";
import { NzbnEntitySearchResponse } from "Hooks/Api/apiBusinessNumber";
import {
	CustomerApplicationForm,
	CustomerApplicationTemplateEntityQueryOptions,
	CustomerApplicatnFormApplicantFields
} from "Hooks/Api/apiCustomerApplications";
import { requestUsersApi } from "Hooks/Api/apiUsers";
import { useCustomerApplicationTemplates } from "Hooks/ApiHooks/useCustomerApplicationTemplates";
import { omit } from "lodash";
import { observer } from "mobx-react";
import { ApplicationEntity } from "Models/Entities";
import { store } from "Models/Store";
import { useEffect, useMemo, useState } from "react";
import { ComboboxOptions } from "Util/Constants";
import { Errors } from "Util/CustomTypes";
import alertToast from "Util/ToastifyUtils";
import { CustomerApplicationFormExtensions } from "Views/Pages/CustomerApplication/CustomerApplicationFormLogic";
import { useCustomerApplicationAuthorisation } from "Views/Pages/CustomerApplication/useCustomerApplicationAuthorisation";
import { BusinessPaymentSelector } from "../BusinessPaymentSelector/BusinessPaymentSelector";
import { Button, Colors, Display } from "../Button/Button";
import ButtonAsyncState from "../Button/ButtonAsyncState";
import { ComboboxOption } from "../Combobox/Combobox";
import ComboboxSetter from "../Combobox/ComboboxSetter";
import FileDownloadLink from "../FileUpload/FileDownloadLink";
import FileUpload from "../FileUpload/FileUpload";
import { Row } from "../FlexComponents/Row";
import { InputGroupTitle } from "../Inputs/InputGroupTitle";
import { AbrOrAsicSearchResponse, AustralianEntitySearchBox } from "../JurisdictionBusinessEntitySearchBox/AustralianEntitySearchBox";
import { NewZealandEntitySearchBox } from "../JurisdictionBusinessEntitySearchBox/NewZealandEntitySearchBox";
import { RadioAlignment } from "../RadioButton/RadioButtonGroup";
import RadioButtonGroupSetter from "../RadioButton/RadioButtonGroupSetter";
import TemplateForm from "../TemplateForm/TemplateForm";
import { useTemplateFormContext } from "../TemplateForm/TemplateFormContext";
import { FormFieldDefinition } from "../TemplateForm/TemplateFormTypes";
import { TextArea } from "../TextArea/TextArea";
import { TextFieldSetter } from "../TextBox/TextFieldSetter";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";

function getSchemaBySelectedId(
	selectedId: string | undefined,
	templates: ApplicationEntity[],
): FormFieldDefinition[] {
	if (!selectedId) return [];

	const selected = templates.find(t => t.id === selectedId);
	if (!selected?.jsonSchema) return [];

	try {
		return JSON.parse(selected.jsonSchema) as FormFieldDefinition[];
	} catch (e) {
		console.warn("Invalid JSON schema for selected template", e);
		return [];
	}
}

const fetchSalesRepOptions = async (filterByCustomerApplicationFormId: string, search?: string): Promise<ComboboxOption<string>[]> => {
	const response = await requestUsersApi({
		searchBy: search,
		searchOn: ['firstName', 'lastName'],
		sortBy: 'firstName',
		sortDescending: false,
		filterByCustomerApplicationFormIds: [filterByCustomerApplicationFormId],
	});

	const users = response.data.data;
	return users
		.map(user => ({
			value: user.id,
			disabled: user.isActive != true,
			display: `${user.firstName} ${user.lastName}`
		}));
}

function getInitialState(props: { initialModelData?: CustomerApplicationForm }) {
	const model: CustomerApplicationForm = props.initialModelData ?? {
		ownerId: store?.getUser?.id,
		sendToApplicant: true,
	};

	const uiControls: CustomerApplicationFormUiControls = props.initialModelData
		? {
			abnRelatesToTrust: !!props.initialModelData?.trusteeAcn,
			nzbnRelatesToTrust: !!props.initialModelData?.trusteeNcn
		}
		: DefaultCustomerApplicationFormUiControls;

	return { model, uiControls };
}


type CustomerApplicationFormUiControls = {
	// UI handles it for both abn and nzbn
	abnRelatesToTrust?: boolean,
	nzbnRelatesToTrust?: boolean,
};

const DefaultCustomerApplicationFormUiControls: CustomerApplicationFormUiControls = {
	abnRelatesToTrust: undefined,
	nzbnRelatesToTrust: undefined,
}

// Error model that has a key for each property on the model except the value is a string for error messages
type CustomerApplicationFormErrors = {
	[K in keyof CustomerApplicationForm]?: string;
};

type CustomerApplicationFormProps = {
	onSubmitRequest: (model: CustomerApplicationForm) => Promise<AxiosResponse<CustomerApplicationForm> | undefined>;
	initialModelData?: CustomerApplicationForm;
}

/**
 * Form form for rendering create/edit customer applications.
 */
const CustomerApplicationFormRenderer = observer((props: CustomerApplicationFormProps) => {
	const {
		setSchema,
		formData: templateFormData,
		setFormData,
		errors: templateErrors,
		setErrors: setTemplateErrors,
		validateAllTemplateFields,
		resetInitialState: resetTemplateFormContextState,
		isReadOnly,
	} = useTemplateFormContext();


	const { model: initialModel, uiControls: initialUiControls } = getInitialState(props);
	const [model, setModel] = useState<CustomerApplicationForm>(initialModel);
	const [uiControls, setUiControls] = useState<CustomerApplicationFormUiControls>(initialUiControls);
	const [modelErrors, setModelErrors] = useState<Partial<CustomerApplicationFormErrors>>({});
	const [abnResultDetail, setAbnResultDetail] = useState<AbrOrAsicSearchResponse>();
	const [trusteeAcnResultDetail, setTrusteeAcnResultDetail] = useState<AbrOrAsicSearchResponse>();
	const [nzbnResultDetail, setNzbnResultDetail] = useState<NzbnEntitySearchResponse>();
	const [trusteeNcnResultDetail, setTrusteeNcnResultDetail] = useState<NzbnEntitySearchResponse>();
	const [files, setFiles] = useState<{ index: number, file: File | undefined }[]>([]);

	const { canEditSubsetOfFieldsOnReadonly } = useCustomerApplicationAuthorisation(store.getUser);

	// Reset state if the super user impersonates an organisation
	useEffect(() => {
		setModel(initialModel);
		setModelErrors({});
		setUiControls(initialUiControls);
		setAbnResultDetail(undefined);
		setTrusteeAcnResultDetail(undefined)
		setNzbnResultDetail(undefined)
		setTrusteeNcnResultDetail(undefined);
		setFiles([]);
		resetTemplateFormContextState();
	}, [store?.impersonatingOrganisationId]);

	const onModelInputChange = (
		fieldName: keyof CustomerApplicationForm,
		value: string | boolean | number | undefined,
		validate: boolean = true) => {
		const updatedModel = {
			...model,
			[fieldName]: value
		};
		setModel(updatedModel);

		if (validate == true) {
			validateUpdatedModel(updatedModel, fieldName, value);
		}
	};

	function validateUpdatedModel(data: CustomerApplicationForm, fieldName: keyof CustomerApplicationForm, value: string | boolean | number | undefined) {
		const fieldValidationError = CustomerApplicationFormExtensions.validateField(data, fieldName, validateFieldExtraDetails);
		setModelErrors(prev => {
			const { [fieldName]: _, ...rest } = prev;
			return fieldValidationError ? { ...rest, [fieldName]: fieldValidationError } : rest;
		});
	}

	// Return the new set of errors for us to perform validation against without rerender loops
	const validateAllModelFields = (): Errors => {
		const newErrors: Errors = {}
		CustomerApplicationFormExtensions.modfiableFields.forEach(fieldName => {
			const fieldValidationError = CustomerApplicationFormExtensions.validateField(model, fieldName, validateFieldExtraDetails)
			if (fieldValidationError) newErrors[fieldName] = fieldValidationError;
		})
		return newErrors;
	}

	const handleOnClickSubmit = async () => {

		// Trigger all validation to validate inputs that have not been touched and exit from submission if heres any errors. 
		const localModelErrors = validateAllModelFields();
		const localTemplateErrors = validateAllTemplateFields();
		setModelErrors(localModelErrors);
		setTemplateErrors(localTemplateErrors);
		if (Object.keys(localModelErrors).length > 0 || Object.keys(localTemplateErrors).length > 0) return;

		// Stitch on data for final model
		model.jsonSchemaSubmission = templateFormData;
		model.files = files.filter(file => file.file).map(file => file.file as File);

		// Handle submission
		try {
			const result = await props.onSubmitRequest(model);
			// MVP Review Application - Only super user goes to review application until the MVP is ready
			if (store.userType === 'SUPER_USER') {
				store.routerHistory.push(`/approve/customerapplications/${result?.data.id}`);
			} else {
				store.routerHistory.push(`/approve/customerapplications`);
			}
		} catch (err) {
			alertToast('Unable to save customer application.', 'error');
		}
	}

	/**
	 * Check if there are any validation error on the model values or the template form values
	 * Note, this does not account for validators on inputs that have not been triggered yet.
	 */
	const hasErrors = () => {
		if (Object.keys(modelErrors).length > 0) return true;
		if (Object.keys(templateErrors).length > 0) return true;
		return false;
	}

	const canSubmit = () => {
		const isModified = true; // Future: handle later
		const canSubmitWhileReadOnly = canEditSubsetOfFieldsOnReadonly && model.reassignedUserId !== initialModel.reassignedUserId;
		return !hasErrors() && isModified && (!isReadOnly || canSubmitWhileReadOnly);
	}

	const reqCustomerApplicationTemplatesFilters: CustomerApplicationTemplateEntityQueryOptions = useMemo(() => {
		return {
			filterByBusinessIds: model.businessId ? [model.businessId] : [],
			filterByUserIds: store.getUser?.id ? [store.getUser.id] : [],
			filterByActive: true,
		}
	}, [model.businessId, store.getUser?.id]);

	const reqCustomerAppTemplates = useCustomerApplicationTemplates(reqCustomerApplicationTemplatesFilters);

	const customerAppTemplate = reqCustomerAppTemplates.data?.data ?? [];
	const customerAppTemplateOptions = customerAppTemplate.map(x => ({
		display: x.applicationName,
		value: x.id,
	})) as ComboboxOption<string>[];
	const selectedCustomerAppTemplate = customerAppTemplate.find(x => model.customerApplicationTemplateId === x.id);
	const jurisdiction = selectedCustomerAppTemplate ? selectedCustomerAppTemplate.countryCode : 'OTHER';
	const validateFieldExtraDetails = {
		sendToApplicant: model.sendToApplicant ?? false,
		jurisdiction: jurisdiction
	};

	const handleCustomerTemplateChange = (value: string) => {
		onModelInputChange('customerApplicationTemplateId', value)

		// Reset and UI / form values that is dependent on the selected CustomerApplicationTemplate.
		const schema = getSchemaBySelectedId(value, customerAppTemplate);
		setSchema(schema);
		setFormData({});
		setUiControls(prev => ({
			...prev,
		}))
		setModel(prev => {
			const { reassignedUserId, ...rest } = prev;
			return rest;
		})
	}

	// If we are no longer sending to applicant, reset the applicant field form values and error values
	const handleOnCheckedSendToApplicant = (value: boolean) => {
		setModel(prev => ({
			...omit(prev, value ? [] : CustomerApplicatnFormApplicantFields),
			sendToApplicant: value,
		}));

		// Clear errors dependent on whether we toggle on/off
		setModelErrors(prev => ({
			...omit(prev, value ? ['abn', 'nzbn'] : [...CustomerApplicatnFormApplicantFields]),
		}));
	}


	const handleOnAuEntityRelatesToTrust = (value: "yes" | "no") => {
		setUiControls(prev => ({
			...prev,
			abnRelatesToTrust: value === "yes"
		}))
		if (value === "no") {
			onModelInputChange('trusteeAcn', undefined);
			setTrusteeAcnResultDetail(undefined);
		}
	}
	const handleOnNzEntityRelatesToTrust = (value: "yes" | "no") => {
		setUiControls(prev => ({
			...prev,
			nzbnRelatesToTrust: value === "yes"
		}))
		if (value === "no") {
			onModelInputChange('trusteeNcn', undefined);
			setTrusteeNcnResultDetail(undefined);
		}
	}

	const addNewFilePicker = () => {
		const nextIndex = files.length > 0 ? files[files.length - 1].index + 1 : 0;
		setFiles([...files, { index: nextIndex, file: undefined }])
	}

	const removeFilePicker = (index: number) => {
		setFiles([...files.filter(oldFile => oldFile.index !== index)])
	}

	// Auto select 1st template if only 1 available
	useEffect(() => {
		if (model.customerApplicationTemplateId == null && customerAppTemplateOptions.length == 1 && customerAppTemplateOptions[0].value != null) {
			handleCustomerTemplateChange(customerAppTemplateOptions[0].value);
		}
	}, [reqCustomerAppTemplates.data])

	return (
		<div className="customer-application">
			<div className="customer-application-form">
				<BusinessPaymentSelector
					filterByOrgId={store.getUser?.organisation?.id}
					filterForEnabledApprove={true}
					type={store.getUser?.organisation?.accountType ?? "INVOICED"}
					value={model.businessId}
					setValue={value => onModelInputChange('businessId', value.businessId)}
					error={modelErrors.businessId}
					isDisabled={!!model.id || isReadOnly}
				/>

				<InputGroupTitle title="CUSTOMER INFORMATION" />
				<ComboboxSetter
					value={model.customerApplicationTemplateId}
					setValue={handleCustomerTemplateChange}
					options={customerAppTemplateOptions}
					getOptionValue={data => data}
					errors={modelErrors.customerApplicationTemplateId}
					label="Customer Application Form Selection"
					placeholder="Select a Customer Application Form"
					loading={reqCustomerAppTemplates.type === 'loading'}
					isRequired
					searchable
					isDisabled={!!model.id || isReadOnly}
				/>

				{jurisdiction === 'AU' && (
					<>
						<AustralianEntitySearchBox
							debounceKey="abn"
							lookupDetail={model.id && model.abn ? {
								lookupMethod: "abn",
								lookupValue: model.abn
							} : undefined}
							errors={modelErrors.abn}
							entityDetail={abnResultDetail}
							updateDetail={(
								newLookupDetail,
								isValid,
								returnEntityDetail,
							) => {
								if (isValid) {
									onModelInputChange('abn', returnEntityDetail.abn);
									setAbnResultDetail(returnEntityDetail);
								}
							}}
							isDisabled={isReadOnly}
						/>

						{model.abn && (
							<RadioButtonGroupSetter
								label="Does this entity relate to a trust?"
								options={ComboboxOptions.Bools}
								value={uiControls.abnRelatesToTrust ? "yes" : "no"}
								setValue={handleOnAuEntityRelatesToTrust}
								alignment={RadioAlignment.HORIZONTAL}
								isDisabled={isReadOnly}
							/>
						)}

						{model.abn && uiControls.abnRelatesToTrust &&
							<AustralianEntitySearchBox
								debounceKey="trusteeAcn"
								lookupDetail={model.id && model.trusteeAcn ? {
									lookupMethod: "acn",
									lookupValue: model.trusteeAcn
								} : undefined}
								entityDetail={trusteeAcnResultDetail}
								updateDetail={(
									newLookupDetail,
									isValid,
									returnEntityDetail,
								) => {
									if (isValid) {
										onModelInputChange('trusteeAcn', returnEntityDetail.acn);
										setTrusteeAcnResultDetail(returnEntityDetail);
									}
								}}
								isDisabled={isReadOnly}
							/>
						}
					</>
				)}

				{jurisdiction === 'NZ' && (
					<>
						<NewZealandEntitySearchBox
							debounceKey="nzbn"
							errors={modelErrors.nzbn}
							lookupValue={model.nzbn ?? ""}
							entityDetail={nzbnResultDetail}
							updateDetail={(
								newLookupDetail,
								isValid,
								returnEntityDetail,
							) => {
								if (isValid) {
									onModelInputChange('nzbn', returnEntityDetail.nzbn);
									setNzbnResultDetail(returnEntityDetail);
								}
							}}
							isDisabled={isReadOnly}
						/>
						{model.nzbn && (
							<RadioButtonGroupSetter
								label="Does this entity relate to a trust?"
								options={ComboboxOptions.Bools}
								value={uiControls.nzbnRelatesToTrust ? "yes" : "no"}
								setValue={handleOnNzEntityRelatesToTrust}
								alignment={RadioAlignment.HORIZONTAL}
							/>
						)}

						{model.nzbn && uiControls.nzbnRelatesToTrust &&
							<NewZealandEntitySearchBox
								debounceKey="trusteeNcn"
								errors={modelErrors.trusteeNcn}
								lookupValue={model.trusteeNcn ?? ""}
								entityDetail={trusteeNcnResultDetail}
								updateDetail={(
									newLookupDetail,
									isValid,
									returnEntityDetail,
								) => {
									if (isValid) {
										onModelInputChange('trusteeNcn', returnEntityDetail.ncn);
										setTrusteeNcnResultDetail(returnEntityDetail);
									}
								}}
								isDisabled={isReadOnly}
							/>
						}
					</>
				)}

				<TemplateForm />

				<Row clzName="section-title-row">
					<InputGroupTitle title="ONLINE APPLICATION" />
					<ToggleSwitch
						label="Send to applicant"
						model={model}
						modelProperty="sendToApplicant"
						onAfterChecked={(evt, checked) => handleOnCheckedSendToApplicant(checked)}
						isDisabled={isReadOnly}
					/>
				</Row>

				{model.sendToApplicant && (
					<>
						<TextFieldSetter
							label="First Name"
							defaultToUndefined
							placeholder="E.g., John"
							value={model.applicantFirstName}
							setValue={value => onModelInputChange('applicantFirstName', value, false)}
							onChangeAndBlur={value => validateUpdatedModel(model, 'applicantFirstName', value.target.value)}
							errors={modelErrors.applicantFirstName}
							isRequired
							isDisabled={isReadOnly}
						/>
						<TextFieldSetter
							label="Surname"
							defaultToUndefined
							placeholder="E.g., Doe"
							value={model.applicantLastName}
							setValue={value => onModelInputChange('applicantLastName', value, false)}
							onChangeAndBlur={value => validateUpdatedModel(model, 'applicantLastName', value.target.value)}
							isRequired
							errors={modelErrors.applicantLastName}
							isDisabled={isReadOnly}
						/>
						<TextFieldSetter
							label="Email Address"
							defaultToUndefined
							placeholder="E.g., customer@business.com"
							value={model.applicantEmail}
							setValue={value => onModelInputChange('applicantEmail', value, false)}
							onChangeAndBlur={value => validateUpdatedModel(model, 'applicantEmail', value.target.value)}

							errors={modelErrors.applicantEmail}
							isRequired
							isDisabled={isReadOnly}
						/>
						<TextFieldSetter
							label="Phone / Mobile"
							placeholder="E.g., 0466000000"
							defaultToUndefined
							value={model.applicantPhone}
							setValue={value => onModelInputChange('applicantPhone', value, false)}
							onChangeAndBlur={value => validateUpdatedModel(model, 'applicantPhone', value.target.value)}
							errors={modelErrors.applicantPhone}
							isDisabled={isReadOnly}
						/>
						<TextFieldSetter
							label="CC Email"
							value={model.applicantCcEmail}
							placeholder="E.g., customer@business.com"
							setValue={value => onModelInputChange('applicantCcEmail', value, false)}
							onChangeAndBlur={value => validateUpdatedModel(model, 'applicantCcEmail', value.target.value)}
							errors={modelErrors.applicantCcEmail}
							isDisabled={isReadOnly}
						/>
					</>
				)}

				<InputGroupTitle title="APPLICATION OWNERSHIP" />

				<TextFieldSetter
					label="Created By"
					isReadOnly
					value={model.owner?.fullName ?? store.getUser?.userDisplayName}
				/>

				<ComboboxSetter
					value={model.reassignedUserId}
					setValue={value => onModelInputChange('reassignedUserId', value)}
					options={AwesomeDebouncePromise(search => fetchSalesRepOptions(model.customerApplicationTemplateId!, search), 300)}
					initialOptions={() => fetchSalesRepOptions(model.customerApplicationTemplateId!)}
					onAfterSet={(_, data) => onModelInputChange('reassignedUserId', data.value as string)}
					getOptionValue={data => data}
					label="Re-assign to another user"
					placeholder="Re-assign the application to another user"
					loading
					searchable
					isClearable
					isDisabled={isReadOnly && !canEditSubsetOfFieldsOnReadonly}
				/>
			</div>
			<div className="customer-application-panel">
				{model.uploadedDocuments && model.uploadedDocuments.length > 0 &&
					<>
						<InputGroupTitle title="EXISTING DOCUMENTS" />
						<ul>
							{
								model.uploadedDocuments.map(document => (
									<li key={document.fileId}><FileDownloadLink fileId={document.fileId} /></li>
								))
							}
						</ul>
					</>}
				<InputGroupTitle title="DOCUMENT UPLOADS" />
				{files.map(file => (
					<>
						<FileUpload
							model={file}
							modelProperty="file"
							label={`${file.file ? file.file?.name : "Select a file"}`}
							labelVisible={file?.file != null}
							disableDropArea
							onAfterChange={(uploadFile: File) => {
								setFiles([
									...files.filter(oldFile => oldFile.index !== file.index),
									{ index: file.index, file: uploadFile }
								])
							}}
							preview
							onDelete={() => removeFilePicker(file.index)}
							isDisabled={isReadOnly}
						/>
					</>
				))}

				{/* Only show add file if there is an empty file picker */}
				{!files.find(x => x.file == null) && (
					<Button
						colors={Colors.Primary}
						display={Display.Solid}
						onClick={addNewFilePicker}
						icon={{ icon: 'plus-2', iconPos: 'icon-left' }}
						disabled={isReadOnly}
					>
						Add file
					</Button>
				)}

				<InputGroupTitle title="NOTES" />
				<TextArea
					model={model}
					modelProperty="notes"
					placeholder="Type your notes"
					isDisabled={isReadOnly}
					onAfterChange={event => onModelInputChange('notes', event.target.value)}
				/>
			</div>
			<div className="customer-application-footer">
				<ButtonAsyncState
					colors={Colors.Primary}
					display={Display.Solid}
					readonly={!canSubmit()}
					onPress={handleOnClickSubmit}
					waitingText="Submitting..."
				>
					Submit
				</ButtonAsyncState>
			</div>
		</div>
	)
});

export default CustomerApplicationFormRenderer;
